import {BaseAssetFieldsFragment} from '@common/hygraph-schema';
import {Video} from './Video.atom';
import {AnalyticEventProps} from '@/types';
import {getOptAsset} from '@/lib/utils/assets';
import {useDisabledHygraphImageLoader, useHygraphImageLoader} from '@/hooks';
import {Picture} from './Picture';
import {ImageProps} from 'next/image';

export interface OptimizedAssetFragment extends BaseAssetFieldsFragment {
	optUrl?: string;
}

export interface AssetProps extends Partial<ImageProps> {
	asset: OptimizedAssetFragment;
	analytics?: AnalyticEventProps;
	className?: string;
	disableAutoplay?: boolean;
	eager?: boolean;
}

export const Asset = ({
	asset,
	analytics,
	className,
	disableAutoplay,
	eager,
	priority,
	...defaultProps
}: AssetProps) => {
	const disabledHygraphImageLoader = useDisabledHygraphImageLoader();
	const hygraphImageLoader = useHygraphImageLoader();

	if (asset) {
		// TODO: consider other delineations like:
		// case type === "application/msword" for mammoth.js converter
		// case type === "application/pdf" or "application/zip" for simple downloads
		// case type === "text/html" or "text/css" for manual DOM updates without code deploys
		const type = asset.mimeType;

		if (type?.startsWith('video/')) {
			return (
				<Video
					video={asset}
					className={className}
					analytics={analytics}
					disableAutoplay={disableAutoplay}
				/>
			);
		}

		const overrideProps: Partial<ImageProps> = {};
		if (!defaultProps.fill) {
			overrideProps.width = defaultProps.width || asset?.width || 0;
			overrideProps.height = defaultProps.height || asset?.height || 0;
		}

		// when mimeType is image/* and by default try to render an image
		return (
			<Picture
				{...defaultProps}
				{...overrideProps}
				className={className}
				src={getOptAsset(asset)?.url || ''}
				unoptimized={!!asset?.optUrl}
				alt={asset?.altText || ''}
				fetchPriority={eager ? 'high' : 'auto'}
				fallbackImage={asset?.fallbackImage}
				mobileImage={asset?.mobileImage}
				loading={priority ? undefined : eager ? 'eager' : 'lazy'}
				loader={
					asset?.optUrl || type?.startsWith('image/svg')
						? disabledHygraphImageLoader
						: hygraphImageLoader
				}
			/>
		);
	}

	return null;
};
